import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import Header from '../../components/Header';
import { tokens } from '../../theme';
import { can } from '../../utils/permissions';

const sum = (rows = []) => rows.reduce((a, r) => a + Number(r.amount || 0), 0);

const Summary = ({ title, value }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Box width="100%" bgcolor={colors.primary[400]} p={2} borderRadius={2}>
      <Typography variant="h3" fontWeight="bold" sx={{ color: colors.grey[100] }}>
        € {Number(value || 0).toFixed(2)}
      </Typography>
      <Typography variant="h4" sx={{ color: colors.greenAccent[500] }}>
        {title}
      </Typography>
    </Box>
  );
};

const SectionList = ({ title, rows = [] }) => {
  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" color="#4cceac">
        {title}
      </Typography>
      {!rows.length ? (
        <Typography variant="body2">No items.</Typography>
      ) : (
        <Box mt={1}>
          <Box display="grid" gridTemplateColumns="2fr 1fr" gap={1} mb={1}>
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#4cceac' }}>
              Name
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#4cceac' }}>
              Amount (€)
            </Typography>
          </Box>
          {rows.map((r, idx) => (
            <Box key={r.id ?? `row-${idx}`} display="grid" gridTemplateColumns="2fr 1fr" gap={1} mb={0.5}>
              <Typography variant="body1">{r.name}</Typography>
              <Typography variant="body1">{Number(r.amount || 0).toFixed(2)}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const CoordinatorExpenseEditor = ({ coordinatorId, countryId, trips, selectedTrip, setSelectedTrip, onSaved }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('token');
  const [items, setItems] = useState([{ name: '', amount: '' }]);
  const [saving, setSaving] = useState(false);

  const addRow = () => setItems([...items, { name: '', amount: '' }]);
  const removeRow = (idx) => setItems(items.filter((_, i) => i !== idx));
  const updateRow = (idx, key, val) => {
    const next = [...items];
    next[idx] = { ...next[idx], [key]: val };
    setItems(next);
  };

  const saveAll = async () => {
    if (!selectedTrip.guideId || !selectedTrip.departureDate) {
      return;
    }
    setSaving(true);
    try {
      const body = {
        guide_id: Number(selectedTrip.guideId),
        coordinator_id: Number(coordinatorId),
        section: 'coordinator_expense',
        items: items
          .filter(({ name, amount }) => name !== '' && amount !== '')
          .map(({ name, amount }) => ({ name, amount: Number(amount || 0) })),
      };
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/api/admin/trip-reports/${countryId}/${selectedTrip.departureDate}/entries`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', token },
          body: JSON.stringify(body),
        }
      );
      const data = await res.json();
      if (data.success && onSaved) {
        onSaved();
        setItems([{ name: '', amount: '' }]);
      }
    } catch (e) {
      console.error('Error saving coordinator expenses', e);
    }
    setSaving(false);
  };

  return (
    <Box mt={3}>
      <Card sx={{ backgroundColor: colors.primary[400] }}>
        <CardContent>
          <Typography variant="h4" fontWeight="bold" color="#4cceac" sx={{ mb: 2 }}>
            Add Coordinator Expenses
          </Typography>
          <Box display="grid" gap={2} mb={2}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <Box>
                <Typography variant="body2" mb={0.5}>
                  Select Trip (Guide + Date)
                </Typography>
                <Select
                  fullWidth
                  size="small"
                  value={`${selectedTrip.guideId}_${selectedTrip.departureDate}`}
                  onChange={(e) => {
                    const [gid, date] = e.target.value.split('_');
                    setSelectedTrip({ guideId: gid, departureDate: date });
                  }}
                  sx={{ backgroundColor: colors.primary[500] }}
                >
                  {trips.map((t) => (
                    <MenuItem key={`${t.guideId}_${t.departureDate}`} value={`${t.guideId}_${t.departureDate}`}>
                      {t.guideName} - {t.departureDate}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>
            <Typography variant="h6" sx={{ mt: 1, mb: 1, textTransform: 'capitalize' }}>
              Coordinator Expenses
            </Typography>
            {items.map((it, idx) => (
              <Box key={idx} display="grid" gap="12px" gridTemplateColumns="2fr 1fr auto" alignItems="center" mb={1}>
                <TextField
                  label="Name"
                  value={it.name}
                  onChange={(e) => updateRow(idx, 'name', e.target.value)}
                  sx={{ backgroundColor: colors.primary[500] }}
                />
                <TextField
                  label="Amount (€)"
                  type="number"
                  value={it.amount}
                  onChange={(e) => updateRow(idx, 'amount', e.target.value)}
                  sx={{ backgroundColor: colors.primary[500] }}
                />
                <Button variant="outlined" color="error" onClick={() => removeRow(idx)}>
                  Remove
                </Button>
              </Box>
            ))}
            <Box display="flex" gap={1} mt={1}>
              <Button variant="outlined" onClick={addRow} sx={{ color: colors.grey[100], borderColor: colors.grey[300] }}>
                + Add
              </Button>
              <Button variant="contained" onClick={saveAll} disabled={saving || !selectedTrip.guideId}>
                {saving ? 'Saving...' : 'Save Expenses'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

const CoordinatorDestinationReport = () => {
  const { coordinatorId, countryId } = useParams();
  const [searchParams] = useSearchParams();
  const startDate = searchParams.get('startDate') || '';
  const endDate = searchParams.get('endDate') || '';

  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [showExpenseEditor, setShowExpenseEditor] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState({ guideId: '', departureDate: '' });
  const [trips, setTrips] = useState([]);
  const [coordinatorExpenses, setCoordinatorExpenses] = useState([{ name: '', amount: '' }]);
  const [savingExpenses, setSavingExpenses] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [openToast, setOpenToast] = useState(false);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError('');
      if (!countryId || countryId === '0') {
        setError('Please provide a valid destination');
        setReport(null);
        return;
      }
      const params = new URLSearchParams();
      if (startDate && endDate) {
        params.append('startDate', startDate);
        params.append('endDate', endDate);
      }
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/api/admin/trip-reports/coordinator/${coordinatorId}/destination/${countryId}?${params.toString()}`,
        { headers: { token } }
      );
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'Failed to load report');
        setReport(null);
        return;
      }
      setReport(data.data);
    } catch (e) {
      console.error('Error fetching coordinator destination report', e);
      setError('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    setExportError('');
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (startDate && endDate) {
        params.append('startDate', startDate);
        params.append('endDate', endDate);
      }
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/api/admin/trip-reports/coordinator/${coordinatorId}/destination/${countryId}/export-excel?${params.toString()}`,
        { headers: { token } }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setExportError(err.message || 'Export failed');
        return;
      }
      const blob = await res.blob();
      const disp = res.headers.get('Content-Disposition');
      const match = disp && disp.match(/filename="?([^";\n]+)"?/);
      const filename = match ? match[1].trim() : `destination-coordinator-report_${coordinatorId}_${countryId}.xlsx`;
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(href);
    } catch (e) {
      console.error('Export Excel error', e);
      setExportError('Export failed');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinatorId, countryId, startDate, endDate]);

  useEffect(() => {
    if (report && report.trips?.length) {
      setTrips(report.trips);
      if (!selectedTrip.guideId && report.trips.length > 0) {
        setSelectedTrip({
          guideId: report.trips[0].guideId,
          departureDate: report.trips[0].departureDate,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report]);

  if (loading) {
    return (
      <Box m="20px" display="flex" justifyContent="center" alignItems="center" height="70vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Header title="COORDINATOR DESTINATION REPORT" subtitle="Aggregated report across assigned guides" />
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{ borderColor: colors.blueAccent[500], color: colors.blueAccent[500] }}
          >
            Back
          </Button>
          {can('reports.destination.export') && (
            <Button
              variant="contained"
              disabled={exporting || !report}
              onClick={handleExportExcel}
              startIcon={<FileDownloadOutlinedIcon />}
              sx={{ backgroundColor: colors.blueAccent[700], ':hover': { backgroundColor: colors.blueAccent[800] } }}
            >
              {exporting ? 'Exporting...' : 'Export Excel'}
            </Button>
          )}
        </Box>
      </Box>

      {error ? (
        <Box mt={2}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : null}
      {exportError ? (
        <Box mt={1}>
          <Typography color="error">{exportError}</Typography>
        </Box>
      ) : null}

      {report ? (
        <>
          <Box mt={2} display="grid" gridTemplateColumns="repeat(4, minmax(0, 1fr))" gap={2}>
            <Summary title="1. Collected amount" value={report.summary.totalAmountCollected} />
            <Summary title="2. Guide fee" value={report.summary.guideFee} />
            <Summary title="Total Expenses" value={report.summary.totalExpense} />
            <Summary title="Net settlement" value={report.summary.netAfterExpenses} />
          </Box>

          <Box mt={2} display="grid" gridTemplateColumns="repeat(4, minmax(0, 1fr))" gap={2}>
            <Summary title="Additional income" value={sum(report.sections.income)} />
            <Summary title="Destination-related costs" value={sum(report.sections.excursion_expense)} />
            <Summary title="Other income" value={sum(report.sections.travel_expense)} />
            <Summary title="Other expenses" value={sum(report.sections.guide_expense)} />
          </Box>

          <Box mt={2} display="grid" gridTemplateColumns="repeat(4, minmax(0, 1fr))" gap={2}>
            <Summary title="Coordinator Expenses" value={sum(report.sections.coordinator_expense)} />
            <Box />
            <Box />
            <Box />
          </Box>

          <Box mt={3}>
            <Card sx={{ backgroundColor: colors.primary[400] }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h4" fontWeight="bold" color="#4cceac">
                    Expense breakdown
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => setShowExpenseEditor(!showExpenseEditor)}
                    sx={{ borderColor: colors.greenAccent[500], color: colors.greenAccent[500] }}
                  >
                    {showExpenseEditor ? 'Hide Editor' : 'Add Coordinator Expenses'}
                  </Button>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap={3}>
                  <SectionList title="Additional income" rows={report.sections.income} />
                  <SectionList title="Destination-related costs" rows={report.sections.excursion_expense} />
                  <SectionList title="Other income" rows={report.sections.travel_expense} />
                  <SectionList title="Other expenses" rows={report.sections.guide_expense} />
                  <SectionList title="Coordinator expenses :" rows={report.sections.coordinator_expense} />
                </Box>
              </CardContent>
            </Card>
          </Box>

          {showExpenseEditor && (
            <CoordinatorExpenseEditor
              coordinatorId={coordinatorId}
              countryId={countryId}
              trips={trips}
              selectedTrip={selectedTrip}
              setSelectedTrip={setSelectedTrip}
              onSaved={() => {
                fetchReport();
                setShowExpenseEditor(false);
                setToastMessage('Coordinator expenses saved successfully');
                setOpenToast(true);
              }}
            />
          )}
        </>
      ) : null}

      <Snackbar
        open={openToast}
        autoHideDuration={4000}
        onClose={() => setOpenToast(false)}
        message={toastMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default CoordinatorDestinationReport;

